import React, { memo, useCallback } from 'react'
import { NavLink, NavLinkProps } from 'react-router-dom'
import { CSSObject, Theme, styled } from '@mui/material/styles'
import MUIDrawer, { DrawerProps, drawerClasses } from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Divider from '@mui/material/Divider'
import { ThemeConsumer } from 'utils/theme'
import {
  PEMRISSION_ROAD_ASSISTANCE,
  PERMISSION_ACCOUNT_READ,
  PERMISSION_ANY,
  PERMISSION_ARCHIVEORDER_READ,
  PERMISSION_COURTESYVEHICLES_READ,
  PERMISSION_CRM_READ,
  PERMISSION_EMAILHISTORY_READ,
  PERMISSION_EXTERNALINVOICE_READ,
  PERMISSION_HISTORY,
  PERMISSION_HISTORYORDER_READ,
  PERMISSION_INVOICE_READ,
  PERMISSION_OKRHISTORY_READ,
  PERMISSION_ORDER_SUGGESTION_READ,
  PERMISSION_PART_READ,
  PERMISSION_RECEIVE_STOCK_READ,
  PERMISSION_RENTALVEHICLES_READ,
  PERMISSION_REPORTING_READ,
  PERMISSION_RMA_READ,
  PERMISSION_SMSHISTORY_READ,
  PERMISSION_STATS_READ,
  PERMISSION_SURVEY_PROVIDERS_READ,
  PERMISSION_TYRE_CHANGE_READ,
  PERMISSION_VEHICLESALES_MODULE,
  PERMISSION_VEHICLE_READ,
  PERMISSION_WARRANTY_CLAIMS_READ,
  PERMISSION_WORKORDER_INVOICE_READ,
  PERMISSION_WORKORDER_QUOTATIONS_READ,
  PERMISSION_WORKORDER_READ
} from 'utils/Subscription/Permissions'
import {
  PERMISSION_APPOINTMENT_READ_OTHERS,
  PERMISSION_APPOINTMENT_READ_PORTAL,
  PERMISSION_ONLY_ORDER_READ,
  PERMISSION_ONLY_PORTAL
} from 'utils/Subscription/PermissionComposites'
import { CrmDashboardURL, CrmSmartAlertsURL } from 'app/crm/RouteConstants'
import { Environment } from '@carsys/enums/environment'
import VehicleSalesCurrencyIcon from 'app/vehicleSales/components_shared/Icons/VehicleSalesCurrencyIcon'
import {
  VehicleSalesGlobalizationURL,
  VehicleSalesInventoryURL,
  VehicleSalesInvoicesURL,
  VehicleSalesOpenTransactionsURL,
  VehicleSalesQuotationsURL
} from 'app/vehicleSales/RouteConstants'
import {
  courtesyVehiclesBookingsURL,
  courtesyVehiclesURL,
  rentalVehiclesContractsURL,
  rentalVehiclesInvoicesURL,
  rentalVehiclesURL
} from 'app/replacementVehicles/RouteConstants'
import { WarrantyClaimsUrl, WorkOrderInvoicesUrl, WorkOrderQuotationsUrl, WorkOrderUrl } from 'app/workorder/RouteConstants'
import { supplierOrdersUrl } from 'app/supplierOrders/routes'
import { CountryCode } from '@carsys/enums/country-code'
import { OrderSystem } from '@cbgms/api/modules/enums'
import HomeIcon from '@mui/icons-material/Home'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import BuildIcon from '@mui/icons-material/Build'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import PersonIcon from '@mui/icons-material/Person'
import DriveEtaIcon from '@mui/icons-material/DriveEta'
import UndoIcon from '@mui/icons-material/Undo'
import StorageIcon from '@mui/icons-material/Storage'
import HistoryIcon from '@mui/icons-material/History'
import SettingsIcon from '@mui/icons-material/Settings'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import BarChartIcon from '@mui/icons-material/BarChart'
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact'
import { SidebarMenuList } from 'app/menu/components/Sidebar/MenuList'
import LogoLKQ from 'assets/svg/LogoLKQ.svg'
import N from 'utils/navigation'
import { SvgIconProps } from '@mui/material/SvgIcon'
import { OrderOpenApprovalsUrl, OrderQuotationsUrl, OrderUrl } from 'app/order/RouteConstants'
import { PurchaseInvoiceUrl } from 'app/purchaseInvoice/RouteConstants'
import { PermissionRequirement } from '@cbgms/base/permissions/permissionsUtils'
import { Feature as FeatureType } from '@cbgms/base/feature-flags/types'
import { StackedIcon } from '@carsys/ui'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import { PoliciesMicroLink } from 'app/settings/components/PrivacySettings/PoliciesMicroLink'
import DrawerHeader from 'app/menu/components/TopBar/DrawerHeader'
import { ClickAwayListener, ListItemButton } from '@mui/material'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { useOrderAccountsContext } from 'app/workorder/contexts/use-order-account-context'
import { useSelector } from 'react-redux'
import { useGetWOMPlusSetting } from 'app/settings/components/WOMPlus/use-wom-plus-settings'
import { IShopWOMPlus } from '@cbgms/api'
import { isAxaptaOrderSystem } from 'utils/Ordersystems/utils'

interface MUIDrawerProps extends DrawerProps {
  drawerwidthopen: number
  drawerwidthclosed: number
}

interface BasicMenuItem {
  name: string
  customIcon?: JSX.Element
  translation: string
  url?: string
  permission: PermissionRequirement
  exact?: boolean
  environments?: Environment[]
  feature?: FeatureType
  shopCountries?: CountryCode | CountryCode[]
  parentUrl?: string
}

export interface ISidebarMenuItem extends BasicMenuItem {
  materialIcon?: (props: SvgIconProps) => JSX.Element
  children?: ISidebarMenuItem[]
}

interface SidebarMenuProps {
  drawerOpen: boolean
  onDrawerOpenToggle: () => void
  drawerWidthOpen: number
  drawerWidthClosed: number
}

const openedMixin = (theme: Theme, drawerWidth: number): CSSObject => ({
  width: drawerWidth,
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  })
})

const closedMixin = (theme: Theme, drawerWidth: number): CSSObject => ({
  width: drawerWidth,
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  })
})

const Drawer = styled(MUIDrawer, {
  shouldForwardProp: prop => prop !== 'open'
})<MUIDrawerProps>(({ theme, open, drawerwidthopen, drawerwidthclosed }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme, drawerwidthopen),
    [`& .${drawerClasses.paper}`]: openedMixin(theme, drawerwidthopen)
  }),
  ...(!open && {
    ...closedMixin(theme, drawerwidthclosed),
    [`& .${drawerClasses.paper}`]: closedMixin(theme, drawerwidthclosed)
  })
}))

const SideBarImg = styled('img')(() => ({
  width: '100%',
  height: '56px',
  objectFit: 'contain'
}))

const SideBarLKQLogo = styled(LogoLKQ)(() => ({
  width: '100%',
  maxWidth: '150px',
  height: 'auto',
  float: 'left',
  marginLeft: '-9px'
}))

const sidebarMenuItemsTop = ({
  shouldShowProductWarrantyClaims,
  orderSystem,
  isPortal
}: {
  shouldShowProductWarrantyClaims: boolean
  orderSystem: string | undefined
  isPortal: boolean
  womData: IShopWOMPlus | undefined
}): ISidebarMenuItem[] => [
  {
    name: 'dashboard',
    materialIcon: HomeIcon,
    translation: 'Dashboard.PageTitle',
    url: '/',
    exact: true,
    permission: PERMISSION_ANY
  },

  /**
   * Diary is for now disabled/hidden on Portal for the production
   * TODO: after Diary is enabled on Portal on production: restore `permission: PERMISSION_APPOINTMENT_READ`
   */
  {
    name: 'calendar20',
    materialIcon: CalendarTodayIcon,
    translation: 'Calendar.Title',
    url: '/scheduler',
    permission: PERMISSION_APPOINTMENT_READ_OTHERS // should be PERMISSION_APPOINTMENT_READ, temporary split to combine with feature flag
  },
  /**
   * Diary is for now disabled/hidden on Portal for the production
   * TODO: after Diary is enabled on Portal on production: remove this 'calendar20-portal' item
   */
  {
    name: 'calendar20-portal',
    materialIcon: CalendarTodayIcon,
    translation: 'Calendar.Title',
    url: '/scheduler',
    permission: PERMISSION_APPOINTMENT_READ_PORTAL, // should be PERMISSION_APPOINTMENT_READ, temporary split to combine with feature flag
    feature: 'diary-portal'
  },
  {
    name: 'workorders',
    materialIcon: BuildIcon,
    translation: 'workorder.title',
    parentUrl: '/workorders',
    permission: PERMISSION_WORKORDER_READ,
    children: [
      {
        name: 'workorders',
        translation: 'workorder.workorders',
        url: WorkOrderUrl,
        exact: true,
        permission: PERMISSION_WORKORDER_READ
      },
      {
        name: 'quotations',
        translation: 'global.sidebar_menu_quotations',
        url: WorkOrderQuotationsUrl,
        exact: true,
        permission: PERMISSION_WORKORDER_QUOTATIONS_READ
      },
      {
        name: 'invoices',
        translation: 'workorder.invoices',
        url: WorkOrderInvoicesUrl,
        exact: true,
        permission: [PERMISSION_WORKORDER_READ, PERMISSION_WORKORDER_INVOICE_READ]
      },
      {
        name: 'claims',
        translation: 'claims.title',
        url: WarrantyClaimsUrl,
        exact: true,
        permission: PERMISSION_WARRANTY_CLAIMS_READ
      }
    ]
  },
  {
    name: 'orders',
    materialIcon: ShoppingCartIcon,
    translation: 'order.active-orders-title',
    permission: PERMISSION_ONLY_ORDER_READ,
    parentUrl: OrderUrl,
    children: [
      {
        name: 'open_orders',
        translation: 'order.open-orders-title',
        exact: true,
        url: OrderUrl,
        permission: PERMISSION_ONLY_ORDER_READ
      },
      {
        name: 'approvals',
        translation: 'order.ApprovalsTitle',
        url: OrderOpenApprovalsUrl,
        permission: PERMISSION_ONLY_ORDER_READ
      },
      {
        name: 'quotations',
        translation: 'order-quotations.title',
        url: OrderQuotationsUrl,
        permission: PERMISSION_ONLY_ORDER_READ
      }
    ]
  },
  {
    name: 'accounts',
    materialIcon: PersonIcon,
    translation: 'Account.PageTitle',
    permission: PERMISSION_ACCOUNT_READ,
    url: '/accounts'
  },
  {
    name: 'vehicles',
    materialIcon: DriveEtaIcon,
    translation: 'vehicle.title',
    parentUrl: '/vehicles',
    permission: PERMISSION_VEHICLE_READ,
    children: [
      {
        name: 'vehicles',
        translation: 'vehicle.title',
        url: '/vehicles',
        exact: true,
        permission: PERMISSION_VEHICLE_READ
      },
      {
        name: 'Road assistance',
        translation: 'RoadAssistance.title',
        url: '/vehicles/road-assistance',
        exact: true,
        permission: [PERMISSION_VEHICLE_READ, PEMRISSION_ROAD_ASSISTANCE],
        shopCountries: CountryCode.NL
      }
    ]
  },
  {
    name: 'rma',
    materialIcon: UndoIcon,
    translation: 'rma.title',
    parentUrl: '/returns',
    permission: {
      PERMISSION_RMA_READ: true,
      PERMISSION_PART_READ: false // If stock menu is not visible, render returns directly in menu. Necessary for portal.
    },
    children: [
      {
        name: 'rma',
        translation: 'rma.title',
        url: '/returns',
        permission: {
          PERMISSION_RMA_READ: true,
          PERMISSION_PART_READ: false
        }
      },
      ...(shouldShowProductWarrantyClaims
        ? [
            {
              name: 'warranty',
              translation: 'productWarrantyClaim.warranties',
              url: '/product-warranty-claims',
              feature: 'product-warranty-claims' as FeatureType,
              exact: true,
              permission: {
                PERMISSION_RMA_READ: true,
                PERMISSION_PART_READ: false
              }
            }
          ]
        : [])
    ]
  },
  ...(isPortal && orderSystem === OrderSystem.VPOM_AxNl
    ? [
        {
          name: 'refurbishment-portal',
          materialIcon: InsertDriveFileIcon,
          translation: 'Dashboard.Refurbishments',
          url: '/forms',
          feature: 'axnl-refurb-request' as FeatureType,
          permission: PERMISSION_ANY
        }
      ]
    : []),
  {
    name: 'stock',
    materialIcon: StorageIcon,
    translation: 'warehouse_part.title',
    parentUrl: '/stock',
    permission: PERMISSION_PART_READ,
    children: [
      {
        name: 'parts',
        translation: 'warehouse_part.title',
        url: '/stock/parts',
        permission: PERMISSION_PART_READ
      },
      {
        name: 'order-suggestion',
        translation: 'OrderSuggestions.Title',
        url: '/stock/order-suggestion',
        permission: PERMISSION_ORDER_SUGGESTION_READ
      },
      {
        name: 'receive-stock',
        translation: 'warehouse.receive_stock_title',
        url: '/stock/receive-stock',
        permission: PERMISSION_RECEIVE_STOCK_READ
      },
      {
        name: 'rma',
        translation: 'rma.title',
        url: '/returns',
        permission: [PERMISSION_PART_READ, PERMISSION_RMA_READ]
      },
      {
        name: 'tyre-change',
        translation: 'TyreStock.PageTitle',
        url: '/stock/tyres',
        permission: PERMISSION_TYRE_CHANGE_READ
      }
    ]
  },
  {
    name: 'crm',
    materialIcon: ConnectWithoutContactIcon,
    translation: 'Crm.Title',
    permission: PERMISSION_CRM_READ,
    url: CrmSmartAlertsURL,
    children: [
      {
        name: 'crm-smart-alerts',
        translation: 'Crm.SmartAlerts',
        exact: true,
        url: CrmSmartAlertsURL,
        permission: PERMISSION_CRM_READ
      },
      {
        name: 'crm-email-and-sms',
        translation: 'Crm.EmailAndSms',
        exact: true,
        url: CrmDashboardURL,
        permission: PERMISSION_CRM_READ
      }
    ]
  },
  {
    name: 'vehiclesales',
    customIcon: <VehicleSalesCurrencyIcon />,
    translation: 'VehicleSales.MenuItem',
    permission: PERMISSION_VEHICLESALES_MODULE,
    parentUrl: '/vehicle-sales',
    children: [
      {
        name: 'inventory',
        translation: 'global.VehicleSalesInventory',
        url: VehicleSalesInventoryURL,
        permission: PERMISSION_VEHICLESALES_MODULE
      },
      {
        name: 'open-transactions',
        translation: 'VehicleSales.OpenTransactionsTitle',
        url: VehicleSalesOpenTransactionsURL,
        permission: PERMISSION_VEHICLESALES_MODULE
      },
      {
        name: 'invoices',
        translation: 'global.sidebar_menu_invoices',
        url: VehicleSalesInvoicesURL,
        permission: PERMISSION_VEHICLESALES_MODULE
      },
      {
        name: 'quotations',
        translation: 'global.sidebar_menu_quotations',
        url: VehicleSalesQuotationsURL,
        permission: PERMISSION_VEHICLESALES_MODULE
      },
      {
        name: 'reports',
        translation: 'VehicleSales.GlobalizationReport',
        url: VehicleSalesGlobalizationURL,
        permission: PERMISSION_VEHICLESALES_MODULE
      }
    ]
  },
  {
    name: 'courtesyVehicles',
    customIcon: <StackedIcon SecondaryIcon={<LocalOfferIcon />} MainIcon={<DriveEtaIcon />} />,
    translation: 'ReplacementVehicles.CourtesyVehicles',
    permission: PERMISSION_COURTESYVEHICLES_READ,
    parentUrl: '/courtesy-vehicles',
    children: [
      {
        name: 'Bookings',
        translation: 'ReplacementVehicles.Bookings',
        url: courtesyVehiclesBookingsURL,
        permission: PERMISSION_COURTESYVEHICLES_READ
      },
      {
        name: 'courtesy-vehicles',
        translation: 'ReplacementVehicles.CourtesyVehicles',
        url: courtesyVehiclesURL,
        exact: true,
        permission: PERMISSION_COURTESYVEHICLES_READ
      }
    ]
  },
  {
    name: 'rentalVehicles',
    customIcon: <StackedIcon SecondaryIcon={<VpnKeyIcon />} MainIcon={<DriveEtaIcon />} />,
    translation: 'ReplacementVehicles.RentalVehicles',
    permission: PERMISSION_RENTALVEHICLES_READ,
    parentUrl: '/rental-vehicles',
    children: [
      {
        name: 'Contracts',
        translation: 'ReplacementVehicles.RentalContracts',
        url: rentalVehiclesContractsURL,
        permission: PERMISSION_RENTALVEHICLES_READ
      },
      {
        name: 'rental-vehicles',
        translation: 'ReplacementVehicles.RentalVehicles',
        url: rentalVehiclesURL,
        exact: true,
        permission: PERMISSION_RENTALVEHICLES_READ
      },
      {
        name: 'rental-invoices',
        translation: 'ReplacementVehicles.RentalInvoices',
        url: rentalVehiclesInvoicesURL,
        exact: true,
        permission: PERMISSION_RENTALVEHICLES_READ
      }
    ]
  }
]

const shouldShowPortalSupplierOrders = (isPortal: boolean, orderSystem: string | undefined): boolean =>
  isPortal && isAxaptaOrderSystem(orderSystem)

const sidebarMenuItemsBottom = ({ isPortal, orderSystem }: { isPortal: boolean; orderSystem: string | undefined }): ISidebarMenuItem[] => [
  {
    name: 'stats',
    materialIcon: TrendingUpIcon,
    translation: 'Global.MenuStats',
    url: '/stats',
    permission: PERMISSION_STATS_READ
  },
  {
    name: 'reporting',
    materialIcon: BarChartIcon,
    translation: 'Reporting.Sidebar_Menu',
    url: '/reporting',
    permission: PERMISSION_REPORTING_READ
  },
  {
    name: 'history',
    materialIcon: HistoryIcon,
    translation: isPortal ? 'global.sidebar_menu_administration' : 'global.sidebar_menu_history',
    permission: PERMISSION_HISTORY,
    parentUrl: '/history',
    children: [
      {
        name: 'invoices',
        translation: 'global.sidebar_menu_invoices',
        url: '/history/invoices',
        permission: PERMISSION_INVOICE_READ
      },
      {
        name: 'financial',
        translation: 'Financial.Financial',
        url: '/financial/externalinvoices',
        permission: PERMISSION_EXTERNALINVOICE_READ
      },
      ...(shouldShowPortalSupplierOrders(isPortal, orderSystem) || orderSystem === OrderSystem.VPOM_EOS
        ? [
            {
              name: 'supplier-orders',
              translation: 'global.sidebar_menu_supplier_orders',
              feature: 'fr0079_enable_supplier_order' as FeatureType,
              url: supplierOrdersUrl,
              permission: PERMISSION_ANY
            }
          ]
        : []),
      {
        name: 'archive',
        translation: isPortal ? 'global.sidebar_menu_stock_closed_order_archive' : 'global.sidebar_menu_stock_order_archive',
        url: '/archive',
        permission: { [PERMISSION_ARCHIVEORDER_READ]: true, [PERMISSION_WORKORDER_READ]: false }
      },
      {
        name: 'returns',
        translation: 'global.sidebar_menu_returns',
        url: '/returns',
        permission: { ...PERMISSION_ONLY_PORTAL, [PERMISSION_RMA_READ]: true }
      },
      {
        name: 'quatations',
        translation: 'global.sidebar_menu_quotations',
        url: OrderQuotationsUrl,
        permission: PERMISSION_ONLY_ORDER_READ
      },
      {
        name: 'emails',
        translation: 'global.sidebar_menu_email_history',
        url: '/history/email',
        permission: PERMISSION_EMAILHISTORY_READ
      },
      ...(shouldShowPortalSupplierOrders(isPortal, orderSystem)
        ? [
            {
              name: 'purchase-invoices',
              translation: 'global.sidebar_menu_purchase_invoices',
              url: PurchaseInvoiceUrl,
              permission: PERMISSION_ANY,
              feature: 'fr0052-enable-invoice-history' as FeatureType
            }
          ]
        : []),
      ...(orderSystem === OrderSystem.VPOM_EOS
        ? [
            {
              name: 'purchase-invoices',
              translation: 'global.sidebar_menu_purchase_invoices',
              url: PurchaseInvoiceUrl,
              permission: PERMISSION_ANY
            }
          ]
        : []),
      {
        name: 'sms',
        translation: 'global.sidebar_menu_sms_history',
        url: '/history/sms',
        permission: PERMISSION_SMSHISTORY_READ
      },
      {
        name: 'orders',
        translation: 'global.sidebar_menu_stock_order_history',
        url: '/history/orders',
        permission: PERMISSION_HISTORYORDER_READ
      },
      {
        name: 'okr',
        translation: 'global.sidebar_menu_okr',
        url: '/history/okr',
        permission: PERMISSION_OKRHISTORY_READ,
        shopCountries: CountryCode.NL
      },
      {
        name: 'surveys',
        translation: 'global.sidebar_menu_survey_history',
        url: '/history/survey-history',
        permission: PERMISSION_SURVEY_PROVIDERS_READ,
        shopCountries: CountryCode.NL
      }
    ]
  },
  {
    name: 'settings',
    materialIcon: SettingsIcon,
    translation: 'global.menu_settings',
    url: '/settings',
    permission: PERMISSION_ANY
  }
]

const LogoNavLink = React.forwardRef((props: NavLinkProps, ref: any) => {
  return <NavLink exact {...props} innerRef={ref} activeClassName='active' />
})

const _SidebarMenu: React.FC<SidebarMenuProps> = ({ drawerOpen, drawerWidthOpen, drawerWidthClosed, onDrawerOpenToggle }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const [popoverID, setPopoverID] = React.useState<string | null>(null)
  const isPortal = useIsPortal()
  const { displayRules } = useOrderAccountsContext()
  const shouldShowProductWarrantyClaims = !!displayRules?.ShowProductWarrantyClaims

  const orderAccountDetails = useSelector((state: any) => state.settings.defaultOrderAccount.details)
  const orderSystem = orderAccountDetails?.Wholesaler?.OrderSystem

  const shopID = useSelector(state => state.auth.currentShop)

  const shopWOMPlusSettings = useGetWOMPlusSetting(shopID)

  const handleClickAnchor = useCallback(
    (event: React.MouseEvent<EventTarget>, ID: string) => {
      if (anchorEl !== event.currentTarget) {
        setAnchorEl(event.currentTarget as HTMLElement)
        setPopoverID(ID)
      }
    },
    [setAnchorEl, setPopoverID]
  )

  const handleCloseAnchor = useCallback(
    (url: string | undefined) => {
      setAnchorEl(null)
      setPopoverID(null)
      if (url) N.push(url)
    },
    [setAnchorEl, setPopoverID]
  )

  const handleDrawerOpen = useCallback(() => {
    onDrawerOpenToggle()
  }, [onDrawerOpenToggle])

  const handleDrawerClose = useCallback(() => {
    setAnchorEl(null)
    setPopoverID(null)
    onDrawerOpenToggle()
  }, [setAnchorEl, setPopoverID, onDrawerOpenToggle])

  return (
    <ClickAwayListener onClickAway={() => !drawerOpen && handleCloseAnchor(undefined)}>
      <Drawer
        open={drawerOpen}
        drawerwidthopen={drawerWidthOpen}
        drawerwidthclosed={drawerWidthClosed}
        variant='permanent'
        sx={{ zIndex: 1200 /* needs zIndex to make sure menu popover will be overlap */ }}
      >
        <Box
          data-testid='sidebar-menu'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between'
          }}
        >
          <Box display='flex' flexDirection='column' flexGrow={1}>
            <List component='nav' disablePadding>
              <DrawerHeader sx={{ p: 0 }}>
                <ListItemButton alignItems='center' disableGutters component={LogoNavLink} to='/' sx={{ p: 0.5 }}>
                  <ThemeConsumer>
                    {theme => <SideBarImg src={drawerOpen ? theme.LogoURL : theme.LogoSmallURL} alt={theme.Name} />}
                  </ThemeConsumer>
                </ListItemButton>
              </DrawerHeader>
            </List>
            <SidebarMenuList
              menuItems={sidebarMenuItemsTop({ shouldShowProductWarrantyClaims, orderSystem, isPortal, womData: shopWOMPlusSettings.data })}
              open={drawerOpen}
              ID={0}
              popoverID={popoverID}
              anchorEl={anchorEl}
              handleClickAnchor={handleClickAnchor}
              handleCloseAnchor={handleCloseAnchor}
            />
          </Box>
          <Box>
            <SidebarMenuList
              menuItems={sidebarMenuItemsBottom({ isPortal, orderSystem })}
              open={drawerOpen}
              ID={1}
              popoverID={popoverID}
              anchorEl={anchorEl}
              handleClickAnchor={handleClickAnchor}
              handleCloseAnchor={handleCloseAnchor}
            />
            <Box>{drawerOpen ? <PoliciesMicroLink /> : <Box sx={{ fontSize: '.8rem' }}>&nbsp;</Box>}</Box>
            <Divider />

            <List component='nav' disablePadding>
              <ListItem
                button
                sx={{ width: '100%', justifyContent: 'space-between' }}
                data-testid='sidebar-menu-collapse-toggle'
                onClick={drawerOpen ? handleDrawerClose : handleDrawerOpen}
              >
                {drawerOpen && (
                  <div className='powered-by'>
                    <SideBarLKQLogo />
                  </div>
                )}
                {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </ListItem>
            </List>
          </Box>
        </Box>
      </Drawer>
    </ClickAwayListener>
  )
}

export const SidebarMenu = memo(_SidebarMenu)
